import React, { useMemo, useState } from "react"
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  MenuItem,
  TextField,
} from "@mui/material"
import { useQuery } from "@tanstack/react-query"
import { apiClient, apiClientHooks } from "../../bootstrapping/InitApiClient"

const UNASSIGNED = '__unassigned__'

export type CaseWorkerAssignDialogProps = {
  open: boolean,
  title: string,
  description?: string,
  customerId: string,
  /** Pre-selected case worker (current assignee), if any */
  currentCaseWorkerProfileId?: string,
  /** Profiles to leave out of the picker (e.g. the person being un-flagged) */
  excludeProfileIds?: string[],
  /** Label for the null choice, e.g. "Unassigned" */
  unassignedLabel?: string,
  submitLabel?: string,
  onClose: () => void,
  onSubmit: (caseWorkerProfileId: string | null) => Promise<void>,
}

/**
 * Picker for assigning a case worker: lists the admins of the customer with
 * flagged case workers first (managers can hold cases too), plus an
 * unassigned option.
 */
export const CaseWorkerAssignDialog: React.FC<CaseWorkerAssignDialogProps> = props => {
  const [selected, setSelected] = useState<string | undefined>(undefined)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | undefined>(undefined)

  const adminProfiles = useQuery({
    queryKey: [...apiClientHooks.getKeyByAlias('getProfiles'), 'customer-admins', props.customerId],
    queryFn: async () => apiClient.getProfiles({
      queries: {
        page_index: '0',
        page_size: '100',
        columnFilters: [{ id: 'admin_customer_ids', value: props.customerId }],
      },
    }),
    enabled: props.open && !!props.customerId,
  })

  const options = useMemo(() => {
    const rows = adminProfiles.data?.rows ?? []
    return rows
      .filter(row => row.active !== false)
      .filter(row => !(props.excludeProfileIds ?? []).includes(row.id))
      .map(row => ({
        id: row.id,
        label: `${row.given_name} ${row.family_name}`.trim() || row.email,
        isCaseWorker: (row.case_worker_customer_ids ?? []).includes(props.customerId),
      }))
      // Flagged case workers first, then alphabetical
      .sort((a, b) => Number(b.isCaseWorker) - Number(a.isCaseWorker) || a.label.localeCompare(b.label))
  }, [adminProfiles.data, props.customerId, props.excludeProfileIds])

  const value = selected ?? props.currentCaseWorkerProfileId ?? UNASSIGNED

  const handleSubmit = async () => {
    setSubmitting(true)
    setError(undefined)
    try {
      await props.onSubmit(value === UNASSIGNED ? null : value)
      props.onClose()
    } catch (err) {
      console.error("CaseWorkerAssignDialog.onSubmit failed: ", err)
      setError('Failed to update the case worker. Please try again.')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Dialog open={props.open} onClose={props.onClose} fullWidth maxWidth="xs">
      <DialogTitle>{props.title}</DialogTitle>
      <DialogContent>
        {props.description && <DialogContentText sx={{ marginBottom: 2 }}>{props.description}</DialogContentText>}
        <TextField
          select
          fullWidth
          label="Case Worker"
          value={value}
          onChange={event => setSelected(event.target.value)}
          margin="normal"
        >
          <MenuItem value={UNASSIGNED}>{props.unassignedLabel ?? 'Unassigned'}</MenuItem>
          {options.map(option => (
            <MenuItem key={option.id} value={option.id}>
              {option.label}{option.isCaseWorker ? '' : ' (manager)'}
            </MenuItem>
          ))}
        </TextField>
        {error && <Alert severity="error" sx={{ marginTop: 1 }}>{error}</Alert>}
      </DialogContent>
      <DialogActions>
        <Button onClick={props.onClose} disabled={submitting}>Cancel</Button>
        <Button onClick={handleSubmit} variant="contained" disabled={submitting || adminProfiles.isLoading}>
          {props.submitLabel ?? 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default CaseWorkerAssignDialog
